/* eslint-disable class-methods-use-this */
import {html, LitElement, nothing, PropertyValues} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {OpenZoomLayerEvent} from '../../@types/dom.js';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import arrowRight from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-24_chevron-right.svg?element';
import arrowLeft from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-24_chevron-left.svg?element';
import close from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-24_close.svg?element';
import {togglePageScrolling} from "../../scripts/utils.js";

@customElement('ents-zoom-layer')
export class ZoomLayer extends LitElement {
  @property({type: Array}) urls?: string[];
  @property({type: String, attribute: 'active-color-name'}) activeColorName?: string;
  @property({type: String, attribute: 'brand-name'}) brandName?: string;
  @property({type: String, attribute: 'product-name'}) productName?: string;
  @property({type: String, attribute: 'close-title'}) closeTitle?: string;
  @property({type: String, attribute: 'arrow-prev-title'}) arrowPrevTitle?: string;
  @property({type: String, attribute: 'arrow-next-title'}) arrowNextTitle?: string;

  private lightbox?: PhotoSwipeLightbox;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('ents:zoom-layer:open', event => this.handleOpenEvent(event));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    togglePageScrolling(true);
    this.lightbox?.destroy();
  }

  firstUpdated() {
    this.initPhotoSwipe();
  }

  updated(changedProperties: PropertyValues) {
    if (changedProperties.has('urls')) {
      this.lightbox?.destroy();
      this.initPhotoSwipe();
    }
  }

  private initPhotoSwipe() {
    if (!this.urls || this.urls.length === 0) return;
    const gallerySelector = this.shadowRoot?.querySelector<HTMLElement>('.pswp-gallery');
    if (!gallerySelector) return;

    this.lightbox = new PhotoSwipeLightbox({
      arrowPrevSVG: arrowLeft().outerHTML,
      arrowNextSVG: arrowRight().outerHTML,
      closeSVG: close().outerHTML,
      gallery: gallerySelector,
      children: 'a',
      imageClickAction: 'zoom',
      zoom: false,
      counter: false,
      padding: {top: 48, bottom: 48, left: 0, right: 0},
      showHideAnimationType: 'none',
      trapFocus: true,
      returnFocus: true,
      secondaryZoomLevel: (zoomLevel) => {
        return Math.min(4 * zoomLevel.initial, 1);
      },
      maxZoomLevel: (zoomLevel) => {
        return Math.min(4 * zoomLevel.initial, 1);
      },
      closeTitle: this.closeTitle ?? 'Close',
      arrowPrevTitle: this.arrowPrevTitle ?? 'Previous',
      arrowNextTitle: this.arrowNextTitle ?? 'Next',
      pswpModule: () => import('photoswipe'),
    });
    this.lightbox.on('uiRegister', () => {
      this.lightbox?.pswp?.element?.setAttribute('aria-modal', 'true');
      this.lightbox?.pswp?.ui?.registerElement({
        name: 'bulletsIndicator',
        className: 'pswp__bullets-indicator',
        appendTo: 'wrapper',
        onInit: (el, pswp) => {
          const bullets: HTMLDivElement[] = [];
          let bullet: HTMLDivElement;
          let prevIndex = -1;

          for (let i = 0; i < pswp.getNumItems(); i++) {
            bullet = document.createElement('div');
            bullet.className = 'pswp__bullet';
            bullet.onclick = (e) => {
              if (!e.target) return;
              pswp.goTo(bullets.indexOf(e.target as HTMLDivElement));
            };
            el.appendChild(bullet);
            bullets.push(bullet);
          }

          pswp.on('change', () => {
            if (prevIndex >= 0) {
              bullets[prevIndex].classList.remove('pswp__bullet--active');
            }
            bullets[pswp.currIndex].classList.add('pswp__bullet--active');
            prevIndex = pswp.currIndex;
          });
        }
      });
    });
    this.lightbox.on('close', () => {
      togglePageScrolling(true);
    });
    this.lightbox.on('destroy', () => {
      togglePageScrolling(true);
    });
    this.lightbox.on('contentAppend', ({content}) => {
      if (content.element) {
        content.element.setAttribute('aria-hidden', 'true');
        content.element.setAttribute('alt', this.getImageAltText());
        content.slide?.container.appendChild(content.element);
      }
    });
    this.lightbox.on('change', () => {
      const element = this.lightbox?.pswp?.element;
      const currentSlide = this.lightbox?.pswp?.currSlide;
      if (element && currentSlide) {
        element.setAttribute('data-test-ents-zoom-image-index', `${currentSlide.index}`);
        element.setAttribute(
          'data-test-ents-zoom-image-source',
          `${currentSlide.data.src?.substring(currentSlide.data.src.lastIndexOf('/') + 1)}`
        );
      }
    });
    this.lightbox.init();
  }

  private handleOpenEvent(event: OpenZoomLayerEvent) {
    if (!this.lightbox || !this.urls || this.urls.length === 0) return;
    togglePageScrolling(false);
    this.lightbox.loadAndOpen(event.detail.index);
  }

  private extractImageDimensionsFromUrl(url: string): { width: string; height: string } {
    const regex = /\/(\d+)x(\d+)\D*\//;
    const match = regex.exec(url);
    if (match) {
      return {width: match[1], height: match[2]};
    }
    return {width: '612', height: '838'};
  }

  private getImageAltText(): string {
    return `${this.brandName} ${this.productName}${this.activeColorName ? ': ' + this.activeColorName : ''}`.trim();
  }

  protected render() {
    if (!this.urls || this.urls.length === 0) return nothing;
    return html`
      <div class="pswp-gallery" style="display: none;">
        ${this.urls.map((url) => {
          const {width, height} = this.extractImageDimensionsFromUrl(url);
          return html`
            <a href="${url}"
               data-pswp-width="${width}"
               data-pswp-height="${height}"
               target="_blank">
              <img src="" alt="${this.getImageAltText()}"/>
            </a>
          `;
        })}
      </div>
    `;
  }
}
